/**
 * useStreaming Hook
 * Manages text chunk accumulation during streaming responses
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { STREAMING_STATE } from '../constants';

/**
 * Hook for managing streaming content accumulation
 * @param {Object} chatService - Chat service instance with on/off methods
 * @returns {Object} Streaming state and content
 */
const useStreaming = (chatService) => {
    const [streamingState, setStreamingState] = useState(STREAMING_STATE.IDLE);
    const [content, setContent] = useState('');
    const [currentMessageId, setCurrentMessageId] = useState(null);
    const [currentRole, setCurrentRole] = useState(null);

    // Ref to track content for callbacks without stale closures
    const contentRef = useRef('');

    useEffect(() => {
        if (!chatService?.on) return;

        // Handle message start
        const unsubStart = chatService.on('textMessageStart', ({ messageId, role }) => {
            setStreamingState(STREAMING_STATE.STREAMING);
            setContent('');
            setCurrentMessageId(messageId);
            setCurrentRole(role);
            contentRef.current = '';
        });

        // Handle content chunks
        const unsubContent = chatService.on('textMessageContent', ({ fullContent }) => {
            setContent(fullContent);
            contentRef.current = fullContent;
        });

        // Handle message end
        const unsubEnd = chatService.on('textMessageEnd', () => {
            setStreamingState(STREAMING_STATE.COMPLETE);
        });

        // Handle run finished
        const unsubFinished = chatService.on('runFinished', () => {
            if (streamingState === STREAMING_STATE.PENDING) {
                setStreamingState(STREAMING_STATE.IDLE);
            }
        });

        // Handle errors
        const unsubError = chatService.on('runError', () => {
            setStreamingState(STREAMING_STATE.ERROR);
        });

        return () => {
            unsubStart?.();
            unsubContent?.();
            unsubEnd?.();
            unsubFinished?.();
            unsubError?.();
        };
    }, [chatService, streamingState]);

    // Reset streaming state
    const reset = useCallback(() => {
        setStreamingState(STREAMING_STATE.IDLE);
        setContent('');
        setCurrentMessageId(null);
        setCurrentRole(null);
        contentRef.current = '';
    }, []);

    // Set to pending state (before response starts)
    const setPending = useCallback(() => {
        setStreamingState(STREAMING_STATE.PENDING);
    }, []);

    // Get current content (useful in callbacks)
    const getCurrentContent = useCallback(() => {
        return contentRef.current;
    }, []);

    return {
        // State
        streamingState,
        content,
        currentMessageId,
        currentRole,

        // Derived state
        isIdle: streamingState === STREAMING_STATE.IDLE,
        isPending: streamingState === STREAMING_STATE.PENDING,
        isStreaming: streamingState === STREAMING_STATE.STREAMING,
        isComplete: streamingState === STREAMING_STATE.COMPLETE,
        hasError: streamingState === STREAMING_STATE.ERROR,

        // Actions
        reset,
        setPending,
        getCurrentContent
    };
};

export default useStreaming;
